import React, { memo, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import PoliticalIllustrations from './PoliticalIllustrations';
import FloatingElements from './FloatingElements';
import useIntersectionObserver from '../hooks/useIntersectionObserver';
import useCounterAnimation from '../hooks/useCounterAnimation';
import { HERO_STATS, ANIMATION_DELAYS } from '../constants';


const Hero = memo(() => {
  const [ref, inView] = useIntersectionObserver();
  const { PoliticalLeader1, PoliticalLeader2, PoliticalLeader3, TrustBadge } = PoliticalIllustrations();

  // Use custom hook for counter animation
  const counters = useCounterAnimation(HERO_STATS, inView);

  // Memoize scroll function to prevent unnecessary re-renders
  const scrollToSection = useCallback((sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  // Memoize animation variants
  const fadeInUp = useMemo(() => ({
    initial: { opacity: 0, y: 50 },
    animate: { opacity: 1, y: 0 }
  }), []);

  const fadeInLeft = useMemo(() => ({
    initial: { opacity: 0, x: -50 },
    animate: { opacity: 1, x: 0 }
  }), []);

  const fadeInRight = useMemo(() => ({
    initial: { opacity: 0, x: 50 },
    animate: { opacity: 1, x: 0 }
  }), []);

  return (
    <section id="home" className="bg-gradient-hero min-h-screen flex items-center pt-20 relative overflow-hidden">
      {/* <MouseSmoke /> */}
      <FloatingElements />
      {/* Parallax Background Elements */}
      <div className="absolute inset-0 parallax-bg">
        <motion.div
          className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-jetBlue-500/20 to-red-500/20 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-gradient-to-r from-orange-500/15 to-jetBlue-600/15 rounded-full blur-3xl"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.2, 0.4, 0.2]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-center relative z-10">
        {/* Premium Badge */}
        <motion.div
          className="inline-flex items-center space-x-3 cinematic-blur rounded-full px-8 py-3 mb-12 political-glow"
          {...fadeInUp}
          transition={{ duration: 0.8, delay: ANIMATION_DELAYS.HERO_BADGE }}
        >
          <div className="w-3 h-3 bg-jetBlue-600 rounded-full animate-pulse"></div>
          <span className="text-sm font-semibold text-white tracking-wide">🏛️ INDIA'S NO. 1 POLITICAL BRANDING AGENCY</span>
        </motion.div>

        {/* Cinematic Main Headline */}
        <motion.h1
          className="hero-text text-white mb-8 leading-none"
          {...fadeInUp}
          transition={{ duration: 0.8, delay: ANIMATION_DELAYS.HERO_TITLE }}
        >
          <span className="typing-text">India's Leading</span>
          <motion.span
            className="block text-gradient-victory mt-2"
            {...fadeInLeft}
            transition={{ duration: 0.8, delay: ANIMATION_DELAYS.HERO_SUBTITLE_1 }}
          >
            Political Branding
          </motion.span>
          <motion.span
            className="block text-white mt-2"
            {...fadeInRight}
            transition={{ duration: 0.8, delay: ANIMATION_DELAYS.HERO_SUBTITLE_2 }}
          >
            Agency
          </motion.span>
        </motion.h1>

        {/* Premium Subtitle */}
        <motion.p
          className="text-xl md:text-2xl text-white/90 mb-6 max-w-4xl mx-auto leading-relaxed font-medium"
          {...fadeInUp}
          transition={{ duration: 0.8, delay: ANIMATION_DELAYS.HERO_DESCRIPTION_1 }}
        >
          Trusted by Ministers, MLAs, and 5500+ Political Leaders


          </motion.p>
        <motion.p
          className="text-lg md:text-xl text-white/80 mb-16 max-w-4xl mx-auto leading-relaxed"
          {...fadeInUp}
          transition={{ duration: 0.8, delay: ANIMATION_DELAYS.HERO_DESCRIPTION_2 }}
        >
          From booth-level strategies to Cabinet-level campaigns, we deliver winning political solutions
          that have secured victory in 88+ elections with a 79% success rate across India.
        </motion.p>

        {/* Premium CTA Buttons */}
        <motion.div
          className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-20"
          {...fadeInUp}
          transition={{ duration: 0.8, delay: ANIMATION_DELAYS.HERO_CTA }}
        >
          <motion.button
            onClick={() => scrollToSection('contact')}
            className="bg-gray-900 hover:bg-jetBlue-900 text-white font-bold py-5 px-10 rounded-2xl transition-all duration-500 border border-gray-700 text-lg"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Talk to Our Strategy Team →
          </motion.button>
          <motion.button
            onClick={() => scrollToSection('wins')}
            className="border-2 border-white/30 text-white hover:bg-white hover:text-jetBlue-900 font-semibold py-5 px-10 rounded-2xl transition-all duration-500 text-lg"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            ▶ View Campaign Wins
          </motion.button>
        </motion.div>


        {/* Political Authority Stats */}
        <div ref={ref} className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto mb-10">
          <motion.div
            className="text-center group"
            initial={{ opacity: 0, y: 80, scale: 0.95 }}
            animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
            transition={{ duration: 0.2, delay: ANIMATION_DELAYS.HERO_STATS_BASE }}
          >
            <div className="text-4xl mb-2 group-hover:scale-110 transition-transform duration-300">🏆</div>
            <div className="text-3xl md:text-4xl font-bold text-white mb-2 group-hover:text-orange-400 transition-colors duration-100">
              {counters.campaigns}+
            </div>
            <div className="text-sm md:text-base text-white/80 leading-tight">Election Campaigns Won</div>
          </motion.div>

          <motion.div
            className="text-center group"
            initial={{ opacity: 0, y: 80, scale: 0.95 }}
            animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
            transition={{ duration: 0.2, delay: ANIMATION_DELAYS.HERO_STATS_BASE + ANIMATION_DELAYS.HERO_STATS_INCREMENT }}
          >
            <div className="text-4xl mb-2 group-hover:scale-110 transition-transform duration-300">👥</div>
            <div className="text-3xl md:text-4xl font-bold text-white mb-2 group-hover:text-orange-400 transition-colors duration-100">
              {counters.politicians}+
            </div>
            <div className="text-sm md:text-base text-white/80 leading-tight">Politicians Trained</div>
          </motion.div>

          <motion.div
            className="text-center group"
            initial={{ opacity: 0, y: 80, scale: 0.95 }}
            animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
            transition={{ duration: 0.2, delay: ANIMATION_DELAYS.HERO_STATS_BASE + (ANIMATION_DELAYS.HERO_STATS_INCREMENT * 2) }}
          >
            <div className="text-4xl mb-2 group-hover:scale-110 transition-transform duration-300">📊</div>
            <div className="text-3xl md:text-4xl font-bold text-white mb-2 group-hover:text-orange-400 transition-colors duration-100">
              {counters.winRatio}%
            </div>
            <div className="text-sm md:text-base text-white/80 leading-tight">Election Winning Ratio (%)</div>
          </motion.div>


          <motion.div
            className="text-center group"
            initial={{ opacity: 0, y: 80, scale: 0.95 }}
            animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
            transition={{ duration: 0.2, delay: ANIMATION_DELAYS.HERO_STATS_BASE + (ANIMATION_DELAYS.HERO_STATS_INCREMENT * 2) }}
          >
            <div className="text-4xl mb-2 group-hover:scale-110 transition-transform duration-300">🗺️</div>
            <div className="text-3xl md:text-4xl font-bold text-white mb-2 group-hover:text-orange-400 transition-colors duration-100">
              {counters.states}+
            </div>
            <div className="text-sm md:text-base text-white/80 leading-tight">States Covered</div>
          </motion.div>
        </div>
      </div>
    </section>
  );
});

export default Hero;
